/*
  window.afPdpVideoSource(video): gives a product video its file just before it plays, and resolves
  when the source is set. Used by the gallery below and by the zoom dialog (assets/product-modal.js),
  so the two cannot disagree.

  The video is printed without a source (data-hls, data-mp4, data-media-id, data-handle:
  snippets/product-thumbnail.liquid, snippets/product-media.liquid). The file is chosen the way the
  product cards choose theirs (pick() in assets/af-card-swipe.js): the smallest rendition that fills
  the box the video is drawn in, and never 1080p (720p at most), from the HLS playlist where the
  browser plays HLS itself, otherwise the MP4 rendition listed in /products/<handle>.js. Liquid only
  exposes the HLS master playlist and the top MP4, and with those the browser streamed 1080p into a
  tile under 400px wide. data-mp4 (the top MP4) is used only if both lookups fail.
*/
if (!window.afPdpVideoSource) {
  window.afPdpVideoSource = (() => {
    /* list: [{ w, h, url }] */
    const pick = (video, list) => {
      if (!list.length) return null;
      list.sort((a, b) => a.w - b.w);
      let allowed = list.filter((item) => item.h <= 720);
      if (!allowed.length) allowed = [list[0]];
      const box = video.closest('.product-media-container') || video;
      const target = (box.clientWidth || 320) * Math.min(window.devicePixelRatio || 1, 2) * 0.6;
      const match = allowed.find((item) => item.w >= target);
      return (match || allowed[allowed.length - 1]).url;
    };
    const fromProduct = (video, fallback) => {
      if (!video.dataset.handle || !window.fetch) return Promise.resolve(fallback);
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      return fetch(`${root}products/${video.dataset.handle}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .then((product) => {
          const list = [];
          ((product && product.media) || []).forEach((media) => {
            if (String(media.id) !== String(video.dataset.mediaId) || !media.sources) return;
            media.sources.forEach((source) => {
              if (source.format === 'mp4' || source.mime_type === 'video/mp4') {
                list.push({ w: source.width, h: source.height, url: source.url });
              }
            });
          });
          return pick(video, list) || fallback;
        })
        .catch(() => fallback);
    };
    return (video) => {
      if (video.afSource) return video.afSource;
      // a video that already has a source (older markup) plays as it is
      if (video.getAttribute('src') || video.querySelector('source')) {
        video.afSource = Promise.resolve();
        return video.afSource;
      }
      const hls = video.dataset.hls;
      const mp4 = video.dataset.mp4;
      const nativeHls =
        hls &&
        window.fetch &&
        (video.canPlayType('application/vnd.apple.mpegurl') || video.canPlayType('application/x-mpegURL'));
      let choose;
      if (nativeHls) {
        const base = new URL(hls, window.location.href);
        choose = fetch(base.toString())
          .then((response) => (response.ok ? response.text() : ''))
          .then((text) => {
            const lines = text.split(/\r?\n/);
            const list = [];
            for (let i = 0; i < lines.length - 1; i++) {
              const info = /^#EXT-X-STREAM-INF:.*RESOLUTION=(\d+)x(\d+)/.exec(lines[i]);
              const next = lines[i + 1].trim();
              if (info && next && next.charAt(0) !== '#') {
                list.push({ w: +info[1], h: +info[2], url: new URL(next, base).toString() });
              }
            }
            // a playlist that could not be read: the MP4 rendition, not the master playlist
            // (from the master the browser climbs to 1080p)
            return pick(video, list) || fromProduct(video, mp4 || base.toString());
          })
          .catch(() => fromProduct(video, mp4 || base.toString()));
      } else {
        choose = fromProduct(video, mp4 || hls);
      }
      video.afSource = choose.then((url) => {
        if (!url) throw new Error('no video source');
        video.src = url;
      });
      return video.afSource;
    };
  })();
}

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.setUpVideos();
        this.guardZoomTaps();
        this.publishHeight();
        this.setUpDots();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.mql.addEventListener('change', this.onBreakpointChange.bind(this));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      /*
        The phone slider's place marker: one small dot per photograph, the current one dark
        (owner, 5 Oct 2026; snippets/product-media-gallery.liquid prints the first paint, the
        rules are in assets/af-pdp.css). The dots follow the slider's own count and position, so
        they stay right when a variant hides or reorders photographs. They are decoration: the
        counter's text is still in the page for assistive technology.
      */
      setUpDots() {
        const viewer = this.elements.viewer;
        const dots = this.querySelector('[data-af-gallery-dots]');
        if (!dots || !viewer || !viewer.slider) return;
        const draw = () => {
          const total = viewer.totalPages || dots.children.length;
          const current = viewer.currentPage || 1;
          while (dots.children.length > total) dots.lastElementChild.remove();
          while (dots.children.length < total) dots.appendChild(document.createElement('span'));
          Array.from(dots.children).forEach((dot, index) => dot.classList.toggle('is-current', index === current - 1));
        };
        // the slider's own scroll and resize handlers were added first and set the page before these run
        viewer.slider.addEventListener('scroll', draw, { passive: true });
        if ('ResizeObserver' in window) new ResizeObserver(draw).observe(viewer.slider);
        this.drawDots = draw;
        draw();
      }

      /*
        Gallery videos play by themselves, muted, looping and inline, while they are on screen
        (owner, 2 Oct 2026). Each has a pause / play button ([data-af-video-toggle],
        snippets/product-thumbnail.liquid).

        From 750px the photos are a grid, and a video starts as soon as a quarter of its tile is
        on screen. Below 750px the gallery is a swipe slider, and a video starts while the shopper
        is swiping to it (a quarter of its slide showing). It stops when it leaves, so nothing
        plays off screen. No video data is requested for a video the shopper has not reached: the
        markup has preload="none", no autoplay attribute and no source.

        Starting on a phone (owner, 3 Oct 2026: it did not start on hers). Nothing here waits for
        the page's load event: the gallery is set up when this script runs. What made the start
        late was the work left for the moment of the swipe: the list of renditions was fetched
        only then, and play() came after that answer. Now the file is chosen ahead of time (in
        the phone slider a moment after the gallery is set up, otherwise when the video's tile
        nears the screen or the shopper first touches the gallery; that request is a few hundred
        bytes of text, not video), so that when the slide arrives
        the video gets its source and play() in the same step. A video whose source is already
        set is played at once, inside the tap when the shopper presses the button, which is what
        an iPhone asks for where it wants a tap (Low Power Mode).

        It does not start by itself for shoppers who ask for data saving, once the shopper has
        paused it, or in the collection quick-add panel (assets/quick-add.js: there a video plays
        only when asked). Where the browser refuses to start it (an iPhone in Low Power Mode, an
        in-app browser that blocks inline video) or has no IntersectionObserver, the video stays a
        poster with the play button, and a tap anywhere on the poster starts it. The video's
        data-af-autoplay attribute says which of these happened (playing, refused, save-data,
        paused-by-shopper), for checks.

        Reduced motion no longer holds it back (owner, 5 Oct 2026). Her own phone has Reduce Motion
        on: the live theme's videos play there and this theme's did not, and she wants them to
        play; the shoppable-video previews made the same change. The pause button on each video
        is that shopper's control (WCAG 2.2.2).

        Source: see window.afPdpVideoSource above.
      */
      setUpVideos() {
        const saveData = Boolean(navigator.connection && navigator.connection.saveData);
        const inPanel = Boolean(this.closest('quick-add-modal'));
        const stopped = new WeakSet(); // videos the shopper paused
        const wanted = new WeakSet(); // videos that should be playing once their source is set
        const videos = Array.from(this.querySelectorAll('video'));
        const note = (video, state) => video.setAttribute('data-af-autoplay', state);
        const start = (video) => {
          video.muted = true;
          let playing;
          try {
            playing = video.play();
          } catch (error) {
            note(video, 'refused');
            return;
          }
          if (playing && typeof playing.then === 'function') {
            playing.then(
              () => note(video, 'playing'),
              (error) => {
                // AbortError: it was paused again before it started (swiped past); not a refusal
                if (error && error.name === 'NotAllowedError') note(video, 'refused');
              }
            );
          }
        };
        const play = (video) => {
          wanted.add(video);
          if (video.afSourceSet) {
            start(video);
            return;
          }
          window
            .afPdpVideoSource(video)
            .then(() => {
              video.afSourceSet = true;
              if (wanted.has(video)) start(video);
            })
            .catch(() => {});
        };
        const pause = (video) => {
          wanted.delete(video);
          if (!video.paused) video.pause();
        };
        // choose the file ahead of the moment it is needed (no video data: preload stays "none")
        const prepare = (video) => {
          if (video.afSourceSet || saveData || inPanel) return;
          window
            .afPdpVideoSource(video)
            .then(() => {
              video.afSourceSet = true;
            })
            .catch(() => {});
        };
        const allowed = (video) => {
          if (inPanel) return false;
          if (saveData) note(video, 'save-data');
          else if (stopped.has(video)) note(video, 'paused-by-shopper');
          else return true;
          return false;
        };
        // how much of the tile has to be on screen
        const share = 0.25;
        const observer =
          'IntersectionObserver' in window
            ? new IntersectionObserver(
                (entries) => {
                  entries.forEach((entry) => {
                    const video = entry.target;
                    if (entry.isIntersecting && entry.intersectionRatio >= share) {
                      if (allowed(video) && video.paused) play(video);
                    } else {
                      pause(video);
                    }
                  });
                },
                { threshold: [0, share, 0.5] }
              )
            : null;
        // a tile about to come on screen (the next row of the grid, the next slide): choose its file
        const nearby =
          'IntersectionObserver' in window
            ? new IntersectionObserver(
                (entries, self) => {
                  entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    prepare(entry.target);
                    self.unobserve(entry.target);
                  });
                },
                { rootMargin: '50% 100%' }
              )
            : null;
        videos.forEach((video) => {
          const button = video.parentElement.querySelector('[data-af-video-toggle]');
          const sync = () => {
            if (!button) return;
            button.classList.toggle('is-paused', video.paused);
            button.setAttribute('aria-label', video.paused ? button.dataset.labelPlay : button.dataset.labelPause);
          };
          video.addEventListener('play', sync);
          video.addEventListener('pause', sync);
          video.removeAttribute('autoplay');
          sync();
          if (observer) observer.observe(video);
          if (nearby) nearby.observe(video);
        });
        if (videos.length) {
          // the shopper has started on the gallery: every video in it gets its file chosen
          const first = () => {
            videos.forEach(prepare);
            this.removeEventListener('touchstart', first);
            this.removeEventListener('pointerdown', first);
          };
          this.addEventListener('touchstart', first, { passive: true });
          this.addEventListener('pointerdown', first, { passive: true });
          // in the phone slider the video is usually the second slide, one swipe away: do not
          // wait for the touch (the page's load event is not waited for either)
          if (!this.mql.matches) window.setTimeout(first, 1200);
        }
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-af-video-toggle]');
          // a tap on the poster of a video that is not playing starts it (it never pauses one)
          const poster = !button && event.target.closest('video.af-pdp-video');
          const video = button ? button.parentElement.querySelector('video') : poster;
          if (!video) return;
          if (video.paused) {
            stopped.delete(video);
            play(video);
          } else if (button) {
            stopped.add(video);
            note(video, 'paused-by-shopper');
            pause(video);
          }
        });
      }

      /*
        A tap on a photograph opens the zoom dialog; a swipe must not (owner, 3 Oct 2026: on her
        phone the page "locked" after swiping the photographs: a swipe had ended as a tap, the
        zoom had opened over the page, and the page behind a dialog does not scroll).
        The browser decides what is a tap, and it still calls a short or late drag a tap. So a
        touch that ends on a photograph opens the zoom only when nothing moved: the finger stayed
        where it came down, neither the photo slider nor the page scrolled during the touch or
        just before it, and the slider is resting on a photograph (not between two). Anything else
        is the end of a swipe and is dropped. Mouse clicks and the keyboard are not filtered.
      */
      guardZoomTaps() {
        const slider = this.querySelector('.product__media-list');
        if (!slider) return;
        let down = null;
        let lastScroll = 0;
        const stamp = () => {
          lastScroll = performance.now();
        };
        slider.addEventListener('scroll', stamp, { passive: true });
        window.addEventListener('scroll', stamp, { passive: true });
        this.addEventListener(
          'pointerdown',
          (event) => {
            down = {
              type: event.pointerType,
              x: event.clientX,
              y: event.clientY,
              left: slider.scrollLeft,
              top: window.scrollY,
              quiet: performance.now() - lastScroll > 300,
            };
          },
          { passive: true, capture: true }
        );
        this.addEventListener(
          'click',
          (event) => {
            if (!event.target.closest('.product__media-toggle')) return;
            const touch = down && down.type !== 'mouse' && event.detail !== 0; // detail 0: keyboard
            if (!touch) return;
            const moved = Math.hypot(event.clientX - down.x, event.clientY - down.y) > 8;
            const scrolled = Math.abs(slider.scrollLeft - down.left) > 1 || Math.abs(window.scrollY - down.top) > 1;
            const width = slider.clientWidth || 1;
            const offset = slider.scrollLeft % width;
            const between = !this.mql.matches && Math.min(offset, width - offset) > 2;
            if (moved || scrolled || between || !down.quiet || performance.now() - lastScroll < 300) {
              event.preventDefault();
              event.stopPropagation();
            }
          },
          true
        );
      }

      /*
        The gallery's height as a custom property, for the desktop layout in assets/af-pdp.css: a
        gallery shorter than the buy box stays pinned beside it, and one taller than the screen has
        to know its own height to pin with its last row in view.
      */
      publishHeight() {
        if (!('ResizeObserver' in window)) return;
        new ResizeObserver(() => {
          this.style.setProperty('--af-pdp-gallery-height', `${this.offsetHeight}px`);
        }).observe(this);
      }

      /*
        A slide change is a swipe in the phone slider. The swiped-to photo becomes the active one,
        so the layouts that show one photo at a time (from 750px, after a rotation or a resize) show
        the photo the shopper was looking at, with the matching thumbnail. From 750px the slider's
        own scroll events are layout changes, not swipes, and are ignored.
      */
      onSlideChanged(event) {
        const current = event.detail.currentElement;
        if (!current || this.mql.matches) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === current);
        });
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${current.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      /* Crossing 750px (rotation, resize): the thumbnail and the photo in view follow the active photo. */
      onBreakpointChange() {
        const active = this.elements.viewer.querySelector('[data-media-id].is-active');
        if (!active) return;
        if (this.mql.matches) {
          this.setActiveThumbnail(this.elements.thumbnails.querySelector(`[data-target="${active.dataset.mediaId}"]`));
        } else {
          // once the slides are side by side again, bring the active one into view
          window.requestAnimationFrame(() => {
            active.parentElement.scrollTo({ left: active.offsetLeft, behavior: 'instant' });
          });
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          if (this.drawDots) this.drawDots();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
      //  window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
